import { useEffect, useMemo, useState } from 'react';
import { ApiError, api, slotLabel, type CredentialingStatus, type LicenseStatus, type RosterCatalog, type RosterPhysician, type RosterProfileInput, type RosterRow } from '../lib/api';
import { navigate } from '../lib/router';
import { useReloadOnChange } from '../lib/changes';

/**
 * The roster, kept by the office (D39). Everything the roster sheet
 * collected, behind a screen: who is on the roster and their status, where
 * each physician is credentialed and for which positions, each state
 * licence with the date it runs out, and the login. The rules read every
 * change at once; every change is recorded with the login behind it.
 */
export function Roster({ abbreviation, editedBy }: { abbreviation: string | null; editedBy: string }) {
  const [rows, setRows] = useState<RosterRow[] | null>(null);
  const [catalog, setCatalog] = useState<RosterCatalog | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<'all' | 'attention' | 'Active' | 'Onboarding' | 'Inactive'>('all');
  const [adding, setAdding] = useState(false);

  const load = () => {
    setError(null);
    Promise.all([api.roster(), api.rosterCatalog()]).then(([r, c]) => { setRows(r); setCatalog(c); }).catch((e: Error) => setError(e.message));
  };
  useEffect(load, []);
  useReloadOnChange(load);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (rows ?? []).filter((r) => {
      if (filter === 'attention' && r.attention.length === 0) return false;
      if (filter !== 'all' && filter !== 'attention' && r.status !== filter) return false;
      if (!needle) return true;
      return `${r.lastName} ${r.firstName} ${r.abbreviation} ${r.email ?? ''}`.toLowerCase().includes(needle);
    });
  }, [rows, q, filter]);

  if (error) return <p className="error">Couldn't load the roster ({error}).</p>;
  if (!rows || !catalog) return <p className="muted">Loading…</p>;
  const siteName = (id: string) => catalog.sites.find((s) => s.id === id)?.name ?? id;
  const open = abbreviation ? rows.find((r) => r.abbreviation === abbreviation) ?? null : null;

  return (
    <section className="roster">
      {/* The Login column falling back has to SAY it is falling back (D96). It shows the plain
          email when the login service cannot be read, which is one small suffix away from the
          answer it gives when it has checked and found nothing — a difference nobody should be
          asked to notice. This is the whole fault the column was rewritten to fix, one level up. */}
      {rows.length > 0 && rows.every((r) => !r.loginKnown) && (
        <p className="error noprint">
          The Login column cannot be checked right now, so it is showing what was recorded here rather than who can
          actually sign in. A login made with <code>infra/add-user.sh</code> would not appear. Run{' '}
          <code>sh infra/set-roster-logins.sh</code> to let the platform read the login list.
        </p>
      )}
      <div className="gridtools">
        <h1>Roster <span className="muted small">{rows.filter((r) => r.status !== 'Inactive').length} active · {rows.filter((r) => r.status === 'Onboarding').length} onboarding · {rows.filter((r) => r.attention.length).length} need a look</span></h1>
        <input className="search" value={q} placeholder="Search by name, abbreviation, email" onChange={(e) => setQ(e.target.value)} />
        <select value={filter} onChange={(e) => setFilter(e.target.value as typeof filter)}>
          <option value="all">Everyone</option>
          <option value="attention">Needs a look</option>
          <option value="Active">Active</option>
          <option value="Onboarding">Onboarding</option>
          <option value="Inactive">Inactive</option>
        </select>
        <button type="button" className="primary" onClick={() => { setAdding(true); navigate('/admin/roster'); }}>Add a physician</button>
      </div>
      {adding && <NewPhysician editedBy={editedBy} onDone={(abbr) => { setAdding(false); load(); if (abbr) navigate(`/admin/roster/${encodeURIComponent(abbr)}`); }} />}
      <div className={`roster-split${open ? ' open' : ''}`}>
        <div className="gridscroll">
          <table className="grid roster-table">
            <thead>
              <tr><th className="sticky name">Physician</th><th>Status</th><th>Credentialed at</th><th>Licences</th><th>Login</th><th>Needs a look</th></tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.abbreviation} className={`st-${r.status.toLowerCase()}${open?.abbreviation === r.abbreviation ? ' selected' : ''}`}>
                  <th className="sticky name">
                    <button className="linklike" type="button" onClick={() => navigate(`/admin/roster/${encodeURIComponent(r.abbreviation)}`)}>{r.lastName}, {r.firstName}</button>
                    <span className="muted small"> {r.abbreviation}{r.employmentType ? ` · ${r.employmentType}` : ''}</span>
                  </th>
                  <td><span className={`pill st-${r.status.toLowerCase()}`}>{r.status}{r.status === 'Onboarding' && r.onboardingStartDate ? ` from ${r.onboardingStartDate}` : ''}</span></td>
                  <td className="small">{r.credentialing.length ? r.credentialing.map((c) => <span key={c.siteId} className={`pill tiny cred-${c.status}`}>{siteName(c.siteId)}{c.status === 'in-process' ? ' (in process)' : ''}</span>) : <span className="muted">—</span>}</td>
                  <td className="small">{r.licenses.length ? r.licenses.map((l) => <span key={l.state} className={`pill tiny lic-${l.status}${l.warning ? ` lic-${l.warning}` : ''}`}>{l.state} {l.status}{l.expiresOn ? ` · ${l.expiresOn}` : ''}</span>) : <span className="muted">—</span>}</td>
                  {/* What the login service says, not what we recorded locally (D95): a login made
                      with infra/add-user.sh never reached the database, so this column used to
                      show an email address for people who could sign in perfectly well. */}
                  <td className="small">
                    {r.hasLogin ? (
                      <span className="ok">{r.loginKnown ? 'can sign in' : 'created'}</span>
                    ) : r.email ? (
                      <span className="muted">{r.loginKnown ? `${r.email} — no login yet` : r.email}</span>
                    ) : (
                      <span className="muted">no email</span>
                    )}
                  </td>
                  <td className="small warn">{r.attention.join(' · ')}</td>
                </tr>
              ))}
              {shown.length === 0 && <tr><td colSpan={6} className="muted">Nobody matches.</td></tr>}
            </tbody>
          </table>
        </div>
        {open && <PhysicianPanel key={open.abbreviation} abbreviation={open.abbreviation} catalog={catalog} editedBy={editedBy} onChanged={load} onClose={() => navigate('/admin/roster')} />}
      </div>
      <p className="legend muted">
        Credentialed at a hospital grants its standard positions (the rounders); backup call only for physicians who take it; specialty and night positions are granted one by one. "In process" grants the same seats provisionally — the rules warn instead of blocking. A licence marked <em>none</em> blocks; <em>pending</em> warns; an expiry date warns 90 days ahead. Every change here is recorded with your name.
      </p>
    </section>
  );
}

const STATUS_OPTIONS: CredentialingStatus[] = ['none', 'in-process', 'credentialed'];
const statusLabel = (s: CredentialingStatus) => (s === 'none' ? 'Not credentialed' : s === 'in-process' ? 'In process' : 'Credentialed');

function PhysicianPanel({ abbreviation, catalog, editedBy, onChanged, onClose }: { abbreviation: string; catalog: RosterCatalog; editedBy: string; onChanged: () => void; onClose: () => void }) {
  const [p, setP] = useState<RosterPhysician | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const load = () => api.rosterOne(abbreviation).then(setP).catch((e: Error) => setError(e.message));
  useEffect(() => { load(); }, [abbreviation]);

  const run = async (key: string, what: () => Promise<RosterPhysician | { message: string }>, done?: string) => {
    setBusy(key); setProblem(null); setNote(null);
    try {
      const r = await what();
      if ('abbreviation' in r) setP(r);
      setNote(done ?? ('message' in r ? r.message : null));
      onChanged();
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  if (error) return <aside className="roster-panel"><p className="error">{error}</p></aside>;
  if (!p) return <aside className="roster-panel"><p className="muted">Loading…</p></aside>;
  const statesForLicences = [...new Set([...catalog.statesInUse, ...p.licenses.map((l) => l.state)])].sort();

  return (
    <aside className="roster-panel">
      <header>
        <div>
          <h2>{p.firstName} {p.lastName} <span className="muted small">{p.abbreviation}</span></h2>
          <span className={`pill st-${p.status.toLowerCase()}`}>{p.status}</span>
        </div>
        <button type="button" className="linklike" onClick={onClose}>close</button>
      </header>
      {problem && <p className="error small">{problem}</p>}
      {note && <p className="ok small">{note}</p>}

      <ProfileForm key={p.abbreviation} p={p} busy={busy === 'profile'} onSave={(input) => run('profile', () => api.rosterUpdate(p.abbreviation, { ...input, editedBy }), 'Profile saved.')} />

      <h3>Login</h3>
      <div className="formactions">
        {p.loginCreatedAt ? <span className="ok small">Login created {new Date(p.loginCreatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — they sign in with {p.email} and a one-time code.</span> : p.email ? <span className="muted small">{p.email} has no login yet.</span> : <span className="muted small">Add an email above to create a login.</span>}
        {catalog.logins.enabled ? (
          <button type="button" disabled={busy === 'login' || !p.email} onClick={() => run('login', () => api.rosterLogin(p.abbreviation, editedBy))}>{busy === 'login' ? 'Creating…' : p.loginCreatedAt ? 'Re-send / repoint login' : 'Create login'}</button>
        ) : (
          <span className="muted small">Logins are not created from here on this service{catalog.logins.problem ? ` (${catalog.logins.problem})` : ''}.</span>
        )}
      </div>

      <h3>Credentialing by hospital</h3>
      <table className="plain cred">
        <thead><tr><th>Hospital</th><th>Status</th><th>Also granted</th><th /></tr></thead>
        <tbody>
          {p.credentialing.map((c) => <CredentialingRow key={c.siteId} c={c} catalog={catalog} busy={busy === `cred:${c.siteId}`} onSave={(status, extraSlots) => run(`cred:${c.siteId}`, () => api.rosterCredentialing(p.abbreviation, c.siteId, { status, extraSlots, editedBy }), `${c.siteName}: ${statusLabel(status).toLowerCase()}.`)} />)}
        </tbody>
      </table>
      <p className="muted small">{p.takesNightBackup ? 'Takes backup call: the backup position is granted wherever they are credentialed.' : 'Does not take backup call (change it on the profile) — no backup position is granted.'}</p>

      <h3>Licences by state</h3>
      <table className="plain lic">
        <thead><tr><th>State</th><th>Status</th><th>Expires</th><th>Number</th><th /></tr></thead>
        <tbody>
          {statesForLicences.map((st) => <LicenseRow key={st} state={st} l={p.licenses.find((x) => x.state === st) ?? null} hospitals={catalog.sites.filter((s) => s.licenseState === st).map((s) => s.name)} busy={busy === `lic:${st}`} onSave={(status, expiresOn, number) => run(`lic:${st}`, () => api.rosterLicense(p.abbreviation, st, { status, expiresOn, number, editedBy }), `${st} licence: ${status}${expiresOn ? `, expires ${expiresOn}` : ''}.`)} />)}
          <AddLicenseRow existing={statesForLicences} states={catalog.states} onAdd={(st) => run(`lic:${st}`, () => api.rosterLicense(p.abbreviation, st, { status: 'active', expiresOn: null, number: null, editedBy }), `${st} licence added as active — set its expiry date.`)} />
        </tbody>
      </table>

      {p.changes.length > 0 && (
        <details className="small">
          <summary>Recent changes ({p.changes.length})</summary>
          <ul className="changes">
            {p.changes.map((c, i) => <li key={i}><span className="muted">{new Date(c.changedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span> · {c.what} · {c.changedBy}{c.after ? <span className="muted"> — {describe(c.after)}</span> : null}</li>)}
          </ul>
        </details>
      )}
    </aside>
  );
}

function describe(v: unknown): string {
  if (v && typeof v === 'object') return Object.entries(v as Record<string, unknown>).map(([k, x]) => `${k}: ${Array.isArray(x) ? x.join(', ') || '—' : String(x ?? '—')}`).join(', ');
  return String(v);
}

function ProfileForm({ p, busy, onSave }: { p: RosterPhysician; busy: boolean; onSave: (input: RosterProfileInput) => void }) {
  const [f, setF] = useState<RosterProfileInput>(() => pick(p));
  const dirty = JSON.stringify(f) !== JSON.stringify(pick(p));
  const set = <K extends keyof RosterProfileInput>(k: K, v: RosterProfileInput[K]) => setF((x) => ({ ...x, [k]: v }));
  return (
    <div className="profile">
      <div className="two">
        <label className="field"><span>First name</span><input value={f.firstName ?? ''} onChange={(e) => set('firstName', e.target.value)} /></label>
        <label className="field"><span>Last name</span><input value={f.lastName ?? ''} onChange={(e) => set('lastName', e.target.value)} /></label>
        <label className="field"><span>Email (their sign-in)</span><input type="email" value={f.email ?? ''} onChange={(e) => set('email', e.target.value)} /></label>
        <label className="field"><span>Mobile</span><input value={f.phone ?? ''} onChange={(e) => set('phone', e.target.value)} /></label>
        <label className="field"><span>Status</span>
          <select value={f.status ?? 'Active'} onChange={(e) => set('status', e.target.value as RosterProfileInput['status'])}><option>Active</option><option>Onboarding</option><option>Inactive</option></select>
        </label>
        <label className="field"><span>Start date {f.status === 'Onboarding' ? '(needed to be schedulable)' : ''}</span><input type="date" value={f.onboardingStartDate ?? ''} onChange={(e) => set('onboardingStartDate', e.target.value || null)} /></label>
        <label className="field"><span>Employment</span>
          <select value={f.employmentType ?? ''} onChange={(e) => set('employmentType', (e.target.value || null) as RosterProfileInput['employmentType'])}><option value="">—</option><option>W2</option><option>1099</option></select>
        </label>
        <label className="field"><span>Standard day shift</span>
          <select value={f.standardDayShiftHours ?? ''} onChange={(e) => set('standardDayShiftHours', (e.target.value ? Number(e.target.value) : null) as RosterProfileInput['standardDayShiftHours'])}><option value="">John's rule (W2 → 12 h, else 16 h)</option><option value="12">12 h (7a–7p)</option><option value="16">16 h (7a–11p)</option></select>
        </label>
        <label className="field check"><input type="checkbox" checked={f.takesNightBackup === true} onChange={(e) => set('takesNightBackup', e.target.checked)} /> Takes backup call</label>
        <label className="field"><span>Typical block</span>
          <select value={f.typicalBlockLength ?? ''} onChange={(e) => set('typicalBlockLength', (e.target.value || null) as RosterProfileInput['typicalBlockLength'])}><option value="">—</option><option>1 week</option><option>2 weeks</option><option>Either</option></select>
        </label>
        <label className="field"><span>Max weeks in a row</span><input type="number" min={1} max={12} value={f.maxConsecutiveWeeks ?? ''} onChange={(e) => set('maxConsecutiveWeeks', e.target.value ? Number(e.target.value) : null)} /></label>
        <label className="field"><span>Target weeks per quarter</span><input type="number" min={0} max={13} value={f.targetWeeksPerQuarter ?? ''} onChange={(e) => set('targetWeeksPerQuarter', e.target.value ? Number(e.target.value) : null)} /></label>
      </div>
      <label className="field"><span>Notes</span><input value={f.notes ?? ''} maxLength={2000} onChange={(e) => set('notes', e.target.value)} /></label>
      <div className="formactions">
        <button type="button" className="primary" disabled={busy || !dirty} onClick={() => onSave(f)}>{busy ? 'Saving…' : 'Save profile'}</button>
        {f.status === 'Inactive' && p.status !== 'Inactive' && <span className="warn small">Inactive means they cannot be scheduled anywhere; their history stays.</span>}
      </div>
    </div>
  );
}

function pick(p: RosterPhysician): RosterProfileInput {
  return { firstName: p.firstName, lastName: p.lastName, email: p.email ?? '', phone: p.phone ?? '', status: p.status as RosterProfileInput['status'], onboardingStartDate: p.onboardingStartDate, employmentType: (p.employmentType ?? null) as RosterProfileInput['employmentType'], standardDayShiftHours: (p.standardDayShiftHours ?? null) as RosterProfileInput['standardDayShiftHours'], takesNightBackup: p.takesNightBackup, typicalBlockLength: (p.typicalBlockLength ?? null) as RosterProfileInput['typicalBlockLength'], maxConsecutiveWeeks: p.maxConsecutiveWeeks, targetWeeksPerQuarter: p.targetWeeksPerQuarter, notes: p.notes ?? '' };
}

function CredentialingRow({ c, catalog, busy, onSave }: { c: RosterPhysician['credentialing'][number]; catalog: RosterCatalog; busy: boolean; onSave: (status: CredentialingStatus, extraSlots: string[]) => void }) {
  const site = catalog.sites.find((s) => s.id === c.siteId);
  const [status, setStatus] = useState<CredentialingStatus>(c.status);
  const [extras, setExtras] = useState<string[]>(c.extraSlots);
  const dirty = status !== c.status || JSON.stringify([...extras].sort()) !== JSON.stringify([...c.extraSlots].sort());
  const toggle = (slot: string) => setExtras((xs) => (xs.includes(slot) ? xs.filter((x) => x !== slot) : [...xs, slot]));
  return (
    <tr className={`cred-${c.status}`}>
      <td><strong>{c.siteName}</strong>{c.licenseState && <span className="muted small"> · {c.licenseState} licence</span>}</td>
      <td><select value={status} onChange={(e) => setStatus(e.target.value as CredentialingStatus)}>{STATUS_OPTIONS.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}</select></td>
      <td className="small">
        {status === 'none' ? <span className="muted">—</span> : (site?.extraSlots ?? []).length === 0 ? <span className="muted">standard positions only</span> : (site?.extraSlots ?? []).map((slot) => (
          <label key={slot} className="check inline"><input type="checkbox" checked={extras.includes(slot)} onChange={() => toggle(slot)} /> {slotLabel(slot)}</label>
        ))}
      </td>
      <td><button type="button" className="small" disabled={busy || !dirty} onClick={() => onSave(status, status === 'none' ? [] : extras)}>{busy ? 'Saving…' : 'Save'}</button></td>
    </tr>
  );
}

function LicenseRow({ state, l, hospitals, busy, onSave }: { state: string; l: RosterPhysician['licenses'][number] | null; hospitals: string[]; busy: boolean; onSave: (status: LicenseStatus, expiresOn: string | null, number: string | null) => void }) {
  const [status, setStatus] = useState<LicenseStatus>(l?.status ?? 'none');
  const [expiresOn, setExpiresOn] = useState(l?.expiresOn ?? '');
  const [number, setNumber] = useState(l?.number ?? '');
  const dirty = status !== (l?.status ?? 'none') || (expiresOn || null) !== (l?.expiresOn ?? null) || (number || null) !== (l?.number ?? null);
  return (
    <tr className={l?.warning ? `lic-${l.warning}` : ''}>
      <td><strong>{state}</strong>{hospitals.length > 0 && <span className="muted small"> · {hospitals.join(', ')}</span>}</td>
      <td><select value={status} onChange={(e) => setStatus(e.target.value as LicenseStatus)}><option value="active">Active</option><option value="pending">Pending</option><option value="none">None</option></select></td>
      <td><input type="date" value={expiresOn} onChange={(e) => setExpiresOn(e.target.value)} />{l?.warning && <span className={`pill tiny lic-${l.warning}`}>{l.warning === 'expired' ? 'expired' : 'expires soon'}</span>}</td>
      <td><input value={number} maxLength={60} placeholder="licence no." onChange={(e) => setNumber(e.target.value)} /></td>
      <td><button type="button" className="small" disabled={busy || !dirty} onClick={() => onSave(status, expiresOn || null, number || null)}>{busy ? 'Saving…' : 'Save'}</button></td>
    </tr>
  );
}

function AddLicenseRow({ existing, states, onAdd }: { existing: string[]; states: string[]; onAdd: (state: string) => void }) {
  const [st, setSt] = useState('');
  const options = states.filter((s) => !existing.includes(s));
  return (
    <tr className="add">
      <td colSpan={5}>
        <span className="muted small">Another state: </span>
        <select value={st} onChange={(e) => setSt(e.target.value)}><option value="">—</option>{options.map((s) => <option key={s} value={s}>{s}</option>)}</select>{' '}
        <button type="button" className="small" disabled={!st} onClick={() => { onAdd(st); setSt(''); }}>Add</button>
      </td>
    </tr>
  );
}

function NewPhysician({ editedBy, onDone }: { editedBy: string; onDone: (abbreviation: string | null) => void }) {
  const [f, setF] = useState({ firstName: '', lastName: '', abbreviation: '', email: '', employmentType: '' as '' | 'W2' | '1099', status: 'Onboarding' as 'Active' | 'Onboarding', onboardingStartDate: '', takesNightBackup: false });
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const suggested = f.lastName.trim() ? `${f.firstName.trim() ? f.firstName.trim()[0] + '. ' : ''}${f.lastName.trim()}` : '';
  const save = async () => {
    setBusy(true); setProblem(null);
    try {
      const r = await api.rosterCreate({ abbreviation: (f.abbreviation || suggested).trim(), firstName: f.firstName, lastName: f.lastName, email: f.email || null, employmentType: f.employmentType || null, status: f.status, onboardingStartDate: f.onboardingStartDate || null, takesNightBackup: f.takesNightBackup, editedBy });
      onDone(r.abbreviation);
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="card newphysician">
      <h2>Add a physician</h2>
      <p className="muted small">Nothing is granted yet — after saving, set where they are credentialed and their licences, then create the login.</p>
      <div className="two">
        <label className="field"><span>First name</span><input value={f.firstName} onChange={(e) => set('firstName', e.target.value)} autoFocus /></label>
        <label className="field"><span>Last name</span><input value={f.lastName} onChange={(e) => set('lastName', e.target.value)} /></label>
        <label className="field"><span>Abbreviation (how they appear on the grid)</span><input value={f.abbreviation} placeholder={suggested || 'e.g. R. Sandhu'} onChange={(e) => set('abbreviation', e.target.value)} /></label>
        <label className="field"><span>Email (their sign-in)</span><input type="email" value={f.email} onChange={(e) => set('email', e.target.value)} /></label>
        <label className="field"><span>Employment</span><select value={f.employmentType} onChange={(e) => set('employmentType', e.target.value as typeof f.employmentType)}><option value="">—</option><option>W2</option><option>1099</option></select></label>
        <label className="field"><span>Status</span><select value={f.status} onChange={(e) => set('status', e.target.value as typeof f.status)}><option>Onboarding</option><option>Active</option></select></label>
        <label className="field"><span>Start date</span><input type="date" value={f.onboardingStartDate} onChange={(e) => set('onboardingStartDate', e.target.value)} /></label>
        <label className="field check"><input type="checkbox" checked={f.takesNightBackup} onChange={(e) => set('takesNightBackup', e.target.checked)} /> Takes backup call</label>
      </div>
      {problem && <p className="error small">{problem}</p>}
      <div className="formactions">
        <button type="button" className="primary" disabled={busy || !f.firstName.trim() || !f.lastName.trim()} onClick={save}>{busy ? 'Saving…' : 'Add to the roster'}</button>
        <button type="button" disabled={busy} onClick={() => onDone(null)}>Cancel</button>
      </div>
    </div>
  );
}
